import { useEffect, useRef, useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { Skeleton, SkeletonGroup } from '@/components/ui/Skeleton';
import {
  deleteChatConversation,
  listChatConversations,
  renameChatConversation,
  type ChatConversationListItem,
} from '@/lib/chat-conversations';
import { cn } from '@/lib/utils';

const formatConversationDate = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const today = new Date();
  return date.toDateString() === today.toDateString()
    ? date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const errorMessage = (error: unknown, fallback: string): string =>
  error instanceof Error && error.message ? error.message : fallback;

/** The side chat's past conversations (22/10): reopen, rename, delete. */
export const AppChatHistory = ({
  activeConversationId,
  onSelect,
  onDeleted,
}: {
  activeConversationId: string | null;
  onSelect: (conversationId: string) => void;
  onDeleted: (conversationId: string) => void;
}) => {
  const [items, setItems] = useState<ChatConversationListItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const renameInputRef = useRef<HTMLInputElement | null>(null);

  const load = async (offset: number) => {
    setLoading(true);
    setError(null);
    try {
      const page = await listChatConversations(offset);
      setItems((current) => (offset === 0 ? page.items : [...current, ...page.items]));
      setHasMore(page.hasMore);
    } catch (loadError) {
      setError(errorMessage(loadError, 'Impossible de charger l’historique.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load(0);
  }, []);

  useEffect(() => {
    if (renamingId) renameInputRef.current?.select();
  }, [renamingId]);

  const startRename = (item: ChatConversationListItem) => {
    setConfirmingDeleteId(null);
    setRenamingId(item.id);
    setRenameValue(item.title);
  };

  const submitRename = async (item: ChatConversationListItem) => {
    if (renamingId !== item.id) return;
    setRenamingId(null);
    if (renameValue.trim() === item.title) return;
    setBusyId(item.id);
    setError(null);
    try {
      const title = await renameChatConversation(item.id, renameValue);
      setItems((current) =>
        current.map((entry) => (entry.id === item.id ? { ...entry, title } : entry))
      );
    } catch (renameError) {
      setError(errorMessage(renameError, 'Impossible de renommer cette conversation.'));
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async (item: ChatConversationListItem) => {
    setBusyId(item.id);
    setError(null);
    try {
      await deleteChatConversation(item.id);
      setItems((current) => current.filter((entry) => entry.id !== item.id));
      setConfirmingDeleteId(null);
      onDeleted(item.id);
    } catch (deleteError) {
      setError(errorMessage(deleteError, 'Impossible de supprimer cette conversation.'));
    } finally {
      setBusyId(null);
    }
  };

  if (loading && items.length === 0) {
    return (
      <SkeletonGroup label="Chargement de l’historique" className="space-y-1 p-2">
        {[72, 56, 64, 48, 60].map((width) => (
          <div key={width} className="px-3 py-2.5">
            <div style={{ width: `${width}%` }}>
              <Skeleton className="h-4 w-full" />
            </div>
          </div>
        ))}
      </SkeletonGroup>
    );
  }

  return (
    <div className="p-2">
      {error && <p className="px-3 py-2 text-sm text-domino-inkSoft">{error}</p>}
      {items.length === 0 && !error && (
        <p className="px-3 py-2.5 text-sm text-domino-inkSoft">Aucune conversation</p>
      )}
      <ul className="space-y-0.5">
        {items.map((item) => {
          const active = item.id === activeConversationId;
          if (confirmingDeleteId === item.id) {
            return (
              <li
                key={item.id}
                className="flex items-center gap-2 rounded-xl bg-domino-muted px-3 py-2"
              >
                <p className="min-w-0 flex-1 truncate text-sm text-domino-ink">
                  Supprimer « {item.title} » ?
                </p>
                <button
                  type="button"
                  disabled={busyId === item.id}
                  onClick={() => void confirmDelete(item)}
                  className="shrink-0 rounded-lg bg-domino-aubergine px-2.5 py-1 text-xs font-medium text-white transition hover:bg-domino-prune disabled:opacity-60"
                >
                  Supprimer
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingDeleteId(null)}
                  className="shrink-0 rounded-lg px-2 py-1 text-xs text-domino-inkSoft transition hover:text-domino-ink"
                >
                  Annuler
                </button>
              </li>
            );
          }
          if (renamingId === item.id) {
            return (
              <li key={item.id} className="px-1 py-0.5">
                <input
                  ref={renameInputRef}
                  value={renameValue}
                  maxLength={200}
                  aria-label="Nom de la conversation"
                  onChange={(event) => setRenameValue(event.target.value)}
                  onBlur={() => void submitRename(item)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      void submitRename(item);
                    }
                    if (event.key === 'Escape') {
                      event.stopPropagation();
                      setRenamingId(null);
                    }
                  }}
                  className="w-full rounded-xl border border-domino-prune bg-domino-white px-3 py-2 text-sm text-domino-ink outline-none"
                />
              </li>
            );
          }
          return (
            <li key={item.id} className="group relative">
              <button
                type="button"
                onClick={() => onSelect(item.id)}
                aria-current={active ? 'true' : undefined}
                className={cn(
                  'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition hover:bg-domino-muted group-hover:pr-16 group-focus-within:pr-16',
                  active && 'bg-domino-lilac/70 hover:bg-domino-lilac/70',
                  busyId === item.id && 'opacity-60'
                )}
              >
                <span className="min-w-0 flex-1 truncate text-sm text-domino-ink">
                  {item.title}
                </span>
                <span className="shrink-0 text-[11px] text-domino-inkSoft group-hover:invisible group-focus-within:invisible">
                  {formatConversationDate(item.updatedAt)}
                </span>
              </button>
              <div className="absolute inset-y-0 right-1.5 hidden items-center gap-0.5 group-hover:flex group-focus-within:flex">
                <button
                  type="button"
                  onClick={() => startRename(item)}
                  aria-label={`Renommer « ${item.title} »`}
                  title="Renommer"
                  className="rounded-lg p-1.5 text-domino-inkSoft transition hover:bg-domino-white hover:text-domino-aubergine"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setRenamingId(null);
                    setConfirmingDeleteId(item.id);
                  }}
                  aria-label={`Supprimer « ${item.title} »`}
                  title="Supprimer"
                  className="rounded-lg p-1.5 text-domino-inkSoft transition hover:bg-domino-white hover:text-domino-aubergine"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </li>
          );
        })}
      </ul>
      {hasMore && (
        <button
          type="button"
          disabled={loading}
          onClick={() => void load(items.length)}
          className="mt-1 w-full rounded-xl px-3 py-2 text-left text-sm text-domino-inkSoft transition hover:bg-domino-muted hover:text-domino-ink"
        >
          {loading ? 'Chargement…' : 'Afficher plus'}
        </button>
      )}
    </div>
  );
};
